import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useState, useEffect } from 'react';
import { AuthOnly, useAuth } from '@/contexts/auth-context';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchCartItems, addOrUpdateCartItem, decreaseCartItem, increaseCartItem, removeCartItem, clearCart, fetchGuestCartItems, addOrUpdateGuestCartItem, decreaseGuestCartItem, increaseGuestCartItem, removeGuestCartItem, createOrder, createGuestOrder, validatePromoCode, checkMemberPricing } from '@/apiEndpoints';
import Plus from '@/icons/Plus';
import Minus from '@/icons/Minus';
import Trash from '@/icons/Trash';
import getStripe from '@/get-stripejs';
import { Input } from '@heroui/input';
import { Checkbox } from '@heroui/checkbox';
import { addToast } from '@heroui/toast';
import MobileBottomNav from '@/components/MobileBottomNav';
import { calculateFeePerUnit, stripeFeeForNet } from '@/utils/fees';
import { currencySymbol } from '@/utils/currency';
import { wrap, pageBg, displayTitle, eyebrow, card as cardSurface, surface, cardTitle, btnPrimary, btnAccent, label as fieldLabel, glow, EmptyState, inputClassNames } from '@/components/ui/design';

const fieldClassNames = {
    input: 'text-sm',
    inputWrapper: 'h-12 rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.08] dark:group-data-[focus=true]:bg-white/[0.08] dark:group-data-[focus=true]:ring-nightAccent/60',
};

const qtyBtn = 'flex h-8 w-8 items-center justify-center rounded-full bg-grey99 text-grey10 ring-1 ring-black/[0.06] transition-colors hover:bg-grey10 hover:text-white disabled:opacity-40 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:hover:bg-white dark:hover:text-nightBg';
const removeBtn = 'flex h-9 w-9 items-center justify-center rounded-full text-grey40 transition-colors hover:bg-red-50 disabled:opacity-40 dark:text-nightMuted dark:hover:bg-red-900/20';

/*
 * Fee System Implementation:
 * 
 * Each event can have:
 * - feeRate: percentage fee (e.g., 5.5 for 5.5%)
 * - feeRateLabel: display name for the fee (e.g., "Service Fee", "Processing Fee")
 * 
 * Example cart item with fees:
 * {
 *   event: {
 *     title: "Concert Event",
 *     feeRate: 5.5,
 *     feeRateLabel: "Service Fee"
 *   },
 *   ticket: {
 *     price: 50.00
 *   },
 *   quantity: 2
 * }
 * 
 * This would calculate:
 * - Subtotal: $100.00 (50 * 2)
 * - Fee: $5.50 (100 * 5.5%)
 * - Total: $105.50
 */
type CartItem = {
    id: string;
    event: {
        title: string;
        image: string;
        feeRate?: number; // Fee rate as a percentage (e.g., 5.5 for 5.5%)
        feeRateLabel?: string; // Fee label (e.g., "Service Fee", "Processing Fee")
        ticketInfoMode?: number; // 0=disabled, 1=optional, 2=required
        liabilityWaiverEnabled?: boolean;
        liabilityWaiverText?: string | null;
        _id?: string;
    };
    ticket: {
        price: number;
        name?: string;
        type?: string;
    };
    quantity: number;
    warning?: string;
    seatUuid?: string; // Seat UUID for seat-specific tickets
    seatNumber?: string; // Human-readable seat number (e.g., "A-12", "VIP-3")
};

interface ValidationErrors {
    name?: string;
    last?: string;
    email?: string;
    phone?: string;
}

export default function Cart() {
    const { user, cart, setCart, guestCartId } = useAuth();
    const userId = user?.userId;
    const isGuest = !userId;

    // Fetch cart items when screen opens
    const { data: cartData = [], isFetching: isLoading, isError, refetch: refetchCartQuery } = useQuery({
        queryKey: isGuest ? ['guestCartItems', guestCartId] : ['cartItems', userId],
        queryFn: () => {
            if (isGuest && guestCartId) {
                return fetchGuestCartItems(guestCartId);
            } else if (userId) {
                return fetchCartItems();
            }
            return Promise.resolve([]);
        },
        enabled: isGuest ? !!guestCartId : !!userId,
        refetchOnMount: 'always',
        staleTime: 0,
    });

    // Update cart state when data is fetched
    useEffect(() => {
        if (Array.isArray(cartData)) {
            setCart(cartData);
        }
    }, [cartData, setCart]);

    // Use cartData for calculations to ensure we have the latest data
    const currentCart = Array.isArray(cartData) && cartData.length > 0 ? cartData : cart;
    const cur = currencySymbol((currentCart as any[])?.[0]?.event?.currency);

    const [details, setDetails] = useState({ name: '', last: '', email: '', phone: '' });
    const [card, setCard] = useState({ name: '', number: '', exp: '', cvv: '' });
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [useCustomInfo, setUseCustomInfo] = useState(false);
    const [usePerTicketInfo, setUsePerTicketInfo] = useState(false);
    const [acceptedWaiversByEventId, setAcceptedWaiversByEventId] = useState<Record<string, boolean>>({});
    const [attendeeInfos, setAttendeeInfos] = useState<Record<string, {
        eventId: string;
        ticketId: string;
        selectedDate: string;
        seatUuid?: string;
        index: number;
        name: string;
        email: string;
        phone?: string;
    }>>({});

    // Promo code state
    const [promoCode, setPromoCode] = useState('');
    const [appliedPromoCode, setAppliedPromoCode] = useState<any>(null);
    const [promoCodeError, setPromoCodeError] = useState('');
    const [isValidatingPromo, setIsValidatingPromo] = useState(false);

    // Member pricing state - automatic, email-based discount, checked without any user action
    const [memberPricingResult, setMemberPricingResult] = useState<any>(null);

    // Pre-fill details for authenticated users
    useEffect(() => {
        if (user && !isGuest) {
            setDetails({
                name: user.firstName || '',
                last: '',
                email: user.email || '',
                phone: ''
            });
        }
    }, [user, isGuest]);

    const refetch = async () => {
        await refetchCartQuery();
        // Re-validate promo code after cart update
        if (appliedPromoCode?.promoCode?.code) {
            await validatePromoCodeAsync(appliedPromoCode.promoCode.code);
        }
    };

    // Promo code validation function
    const validatePromoCodeAsync = async (code: string) => {
        if (!code.trim()) {
            setPromoCodeError('');
            setAppliedPromoCode(null);
            return;
        }

        setIsValidatingPromo(true);
        setPromoCodeError('');

        try {
            // Note: Backend now calculates discount based on matching items only
            // We still send a total amount for reference, but backend ignores it for discount calculation
            const totalAmount = currentCart.reduce((sum, item) => sum + item.ticket.price * item.quantity, 0);

            const validationResult = await validatePromoCode({
                code: code.trim(),
                userId: userId,
                guestCartId: guestCartId,
                orderAmount: totalAmount, // Backend will calculate based on matching items only
                email: buyerEmail || undefined, // Lets the backend block combining with an exclusive member discount
            });

            console.log('🔍 Promo Code Validation Result:', validationResult);
            console.log('🔍 Ticket Discounts:', validationResult.ticketDiscounts);

            if (validationResult.isValid) {
                setAppliedPromoCode(validationResult);
                setPromoCodeError('');
            } else {
                setAppliedPromoCode(null);
                setPromoCodeError(validationResult.error || 'Invalid promo code');
            }
        } catch (error) {
            setAppliedPromoCode(null);
            setPromoCodeError('Error validating promo code');
        } finally {
            setIsValidatingPromo(false);
        }
    };

    // Handle promo code input change
    const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPromoCode(value);

        // Debounce validation
        /* const timeoutId = setTimeout(() => {
            validatePromoCodeAsync(value);
        }, 500); */

        //return () => clearTimeout(timeoutId);
    };

    // Remove applied promo code
    const removePromoCode = () => {
        setPromoCode('');
        setAppliedPromoCode(null);
        setPromoCodeError('');
    };

    // Member pricing is matched automatically off the buyer's email - no code entry, no button.
    const buyerEmail = (isGuest || useCustomInfo) ? details.email.trim() : (user?.email || '');
    useEffect(() => {
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail);
        if (!isValidEmail || currentCart.length === 0) {
            setMemberPricingResult(null);
            return;
        }
        const timeoutId = setTimeout(async () => {
            try {
                const items = currentCart.map((item: any) => ({
                    eventId: item.eventId || item.event?._id || '',
                    ticketId: item.ticketId || item.ticket?._id || '',
                    quantity: item.quantity,
                })).filter((i: any) => i.eventId && i.ticketId);
                if (items.length === 0) return;
                const result = await checkMemberPricing({ email: buyerEmail, items });
                setMemberPricingResult(result);
            } catch {
                setMemberPricingResult(null);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerEmail, currentCart.length]);

    const getMemberPricingForTicket = (ticketId: string) => {
        return memberPricingResult?.tickets?.find((t: any) => t.ticketId === ticketId && t.isMatch) || null;
    };

    const requiredTicketInfoMode = Math.max(
        0,
        ...(currentCart as any[]).map((i) => Number(i?.event?.ticketInfoMode || 0))
    );
    const perTicketInfoEnabled = requiredTicketInfoMode === 2 ? true : usePerTicketInfo;

    const getAttendeeKey = (args: { eventId: string; ticketId: string; selectedDate: string; seatUuid?: string; index: number }) => {
        return `${args.eventId}:${args.ticketId}:${args.selectedDate}:${args.seatUuid || ''}:${args.index}`;
    };

    const getItemAttendeeSlots = (item: any) => {
        const eventId = String(item.eventId || item.event?._id || item.id || '');
        const ticketId = String(item.ticketId || item.ticket?._id || '');
        const selectedDate = String(item.selectedDate || '');
        const seatUuid = item.seatUuid ? String(item.seatUuid) : undefined;
        const fieldsCount = seatUuid ? 1 : Number(item.quantity || 1);
        return Array.from({ length: fieldsCount }, (_, index) => ({
            key: getAttendeeKey({ eventId, ticketId, selectedDate, seatUuid, index }),
            eventId,
            ticketId,
            selectedDate,
            seatUuid,
            index,
        }));
    };

    const currentAttendeeSlots = (currentCart as any[]).flatMap(getItemAttendeeSlots);

    const buildAttendeePayload = () => {
        if (!perTicketInfoEnabled) return undefined;
        return currentAttendeeSlots.map(({ key, ...slot }) => {
            const val = attendeeInfos[key];
            return {
                ...slot,
                name: (val?.name || '').trim(),
                email: (val?.email || '').trim(),
                phone: (val?.phone || '').trim(),
            };
        });
    };

    // Validation function
    const validateForm = (): boolean => {
        const errors: ValidationErrors = {};

        // Validate for guests or authenticated users with custom info
        if (isGuest || useCustomInfo) {
            if (!details.name.trim()) {
                errors.name = 'Name is required';
            }
            if (!details.last.trim()) {
                errors.last = 'Last name is required';
            }
            if (!details.email.trim()) {
                errors.email = 'Email is required';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) {
                errors.email = 'Please enter a valid email address';
            }
            if (!details.phone.trim()) {
                errors.phone = 'Phone number is required';
            } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(details.phone.replace(/\s/g, ''))) {
                errors.phone = 'Please enter a valid phone number';
            }
        }

        setValidationErrors(errors);
        setShowValidationErrors(true);

        // Validate per-ticket attendee info if enabled/required
        if (perTicketInfoEnabled) {
            const missing = currentAttendeeSlots.some(({ key }) => !attendeeInfos[key]?.name?.trim());

            if (missing) {
                addToast({
                    title: 'Missing attendee info',
                    description: 'Please fill attendee name for each ticket.',
                    color: 'danger',
                });
                return false;
            }
        }

        return Object.keys(errors).length === 0;
    };

    // Mutations for cart actions
    const increaseMutation = useMutation<
        void,
        Error,
        { eventId: string; ticketId: string; selectedDate: string }
    >({
        mutationFn: async (item) => {
            if (isGuest && guestCartId) {
                await increaseGuestCartItem(guestCartId, item.eventId, item.ticketId, item.selectedDate);
            } else {
                await increaseCartItem(item.eventId, item.ticketId, item.selectedDate);
            }
        },
        onSuccess: refetch,
    });
    const decreaseMutation = useMutation<
        void,
        Error,
        { eventId: string; ticketId: string, selectedDate: string }
    >({
        mutationFn: async (item) => {
            if (isGuest && guestCartId) {
                await decreaseGuestCartItem(guestCartId, item.eventId, item.ticketId, item.selectedDate);
            } else {
                await decreaseCartItem(item.eventId, item.ticketId, item.selectedDate);
            }
        },
        onSuccess: refetch,
    });
    const removeMutation = useMutation<
        void,
        Error,
        { eventId: string; ticketId: string, selectedDate: string }
    >({
        mutationFn: async (item) => {
            if (isGuest && guestCartId) {
                await removeGuestCartItem(guestCartId, item.eventId, item.ticketId, item.selectedDate);
            } else {
                await removeCartItem(item.eventId, item.ticketId, item.selectedDate);
            }
        },
        onSuccess: refetch,
    });
    const checkoutMutation = useMutation({
        mutationFn: async () => {
            // Validate form before proceeding
            if (!validateForm()) {
                throw new Error('Please fill in all required fields correctly');
            }

            if (!allWaiversAccepted) {
                throw new Error('Please accept the liability waiver to continue');
            }

            if (isGuest && guestCartId) {
                // Use details from form for guest order
                return await createGuestOrder({
                    guestCartId,
                    guestName: `${details.name} ${details.last}`.trim(),
                    guestEmail: details.email,
                    guestPhone: details.phone,
                    promoCode: appliedPromoCode?.promoCode?.code || null,
                    attendees: buildAttendeePayload(),
                    acceptedLiabilityWaiver: allWaiversAccepted,
                    acceptedLiabilityWaiverAt: new Date().toISOString(),
                    ...details,
                });
            } else {
                // For authenticated users, use custom info if enabled, otherwise use account info
                const customerInfo = useCustomInfo ? {
                    customerEmail: details.email,
                    customerPhone: details.phone,
                    customerName: `${details.name} ${details.last}`.trim(),
                    promoCode: appliedPromoCode?.promoCode?.code || null,
                    attendees: buildAttendeePayload(),
                    acceptedLiabilityWaiver: allWaiversAccepted,
                    acceptedLiabilityWaiverAt: new Date().toISOString(),
                } : {
                    customerEmail: user?.email || '',
                    customerPhone: '', // Phone not stored in user profile
                    customerName: user?.firstName || '',
                    promoCode: appliedPromoCode?.promoCode?.code || null,
                    attendees: buildAttendeePayload(),
                    acceptedLiabilityWaiver: allWaiversAccepted,
                    acceptedLiabilityWaiverAt: new Date().toISOString(),
                };

                return await createOrder(customerInfo);
            }
        },
        onSuccess: async (res) => {
            console.log(res?.data);

            // If Stripe checkoutUrl is present, redirect to it. Otherwise, use sessionId with Stripe.js
            const sessionId = res?.data?.stripe?.sessionId || res?.data?.sessionId;
            if (sessionId) {
                const stripe = await getStripe(res?.data?.stripe?.publishableKey);
                await stripe?.redirectToCheckout({ sessionId });
            } else {
                // For zero-total orders, redirect to order result page
                if (res?.data?.order?._id) {
                    // Clear cart first
                    await refetch();
                    // Redirect to order result page using the order's _id
                    window.location.href = `/order?id=${res.data.order._id}&from=payment`;
                } else {
                    // Fallback: just refetch cart
                    refetch();
                }
            }
        },
        onError: (err) => {
            // Handle validation errors differently
            if (err.message === 'Please fill in all required fields correctly') {
                // Validation errors are already shown, just scroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else if (err.message === 'Please accept the liability waiver to continue') {
                addToast({
                    title: 'Liability waiver required',
                    description: 'Please accept the liability waiver to complete checkout.',
                    color: 'danger',
                });
            } else {
                console.log(err);
                alert(err);
            }
        },
    });

    // Helper function to get discount for a specific ticket
    const getTicketDiscount = (ticketId: string) => {
        if (!appliedPromoCode?.ticketDiscounts) return null;
        return appliedPromoCode.ticketDiscounts.find((discount: any) => discount.ticketId === ticketId);
    };

    // Per-unit discount breakdown for one cart item, replicating the backend's combination rule
    // (orders.service.ts): member pricing wins by default; if the ticket's
    // allowPromoCodeWithMemberPricing is set, the promo code stacks on the member-discounted
    // remainder instead of being ignored. Keeping this identical to the backend is what makes
    // the total shown here match what Stripe actually charges.
    const getItemDiscountPerUnit = (item: any) => {
        const ticketId = item.ticketId || item.ticket?._id || '';
        const unitPrice = Number(item?.ticket?.price || 0);
        const memberMatch = getMemberPricingForTicket(ticketId);
        const memberPerUnit = memberMatch?.discountAmountPerUnit || 0;

        const ticketPromo = getTicketDiscount(ticketId);
        const promoDiscountType = appliedPromoCode?.discountType;
        const promoDiscountValue = appliedPromoCode?.discountValue;
        const promoPerUnitOnFull = (ticketPromo && promoDiscountType && promoDiscountValue)
            ? (promoDiscountType === 'fixed' ? Math.min(promoDiscountValue, unitPrice) : (unitPrice * promoDiscountValue) / 100)
            : 0;

        let memberContribution = 0;
        let promoContribution = 0;

        if (memberPerUnit > 0 && promoPerUnitOnFull > 0 && memberMatch.allowPromoCodeWithMemberPricing) {
            const remaining = Math.max(0, unitPrice - memberPerUnit);
            promoContribution = promoDiscountType === 'fixed'
                ? Math.min(promoDiscountValue, remaining)
                : (remaining * promoDiscountValue) / 100;
            memberContribution = memberPerUnit;
        } else if (memberPerUnit > 0) {
            memberContribution = memberPerUnit;
        } else {
            promoContribution = promoPerUnitOnFull;
        }

        const finalUnitPrice = Math.max(0, unitPrice - memberContribution - promoContribution);
        return { unitPrice, memberContribution, promoContribution, finalUnitPrice };
    };

    const subtotal = currentCart.reduce((sum, item) => sum + Number(item?.ticket?.price || 0) * item.quantity, 0);

    // Total member pricing discount (automatic, per matching ticket)
    const memberPricingDiscount = currentCart.reduce((sum, item: any) => sum + getItemDiscountPerUnit(item).memberContribution * item.quantity, 0);

    // Calculate promo code discount - on the member-discounted remainder when stacking is allowed,
    // 0 for a ticket where an exclusive member-pricing match already applies
    const promoDiscount = currentCart.reduce((sum, item: any) => sum + getItemDiscountPerUnit(item).promoContribution * item.quantity, 0);
    // Card processing fees and 'included' service fees are part of the ticket price; 'added' service fees are charged on top
    const discountedTotal = Math.max(0, subtotal - promoDiscount - memberPricingDiscount);

    // One buyer-facing fee: organizer service fee (events with an added fee) plus Stripe's processing cost, grossed up so the
    // tickets and service fee remain whole after Stripe's cut. Tax is not shown.
    const organizerFee = currentCart.reduce((sum, item: any) => {
        const taxInfo = item?.event?.taxInfo;
        if (taxInfo?.feeMode !== 'added') return sum;
        return sum + calculateFeePerUnit(taxInfo?.resolvedFee, getItemDiscountPerUnit(item).finalUnitPrice) * item.quantity;
    }, 0);
    const stripeInfo = (currentCart as any[]).find((item) => item?.event?.taxInfo?.feeMode === 'added')?.event?.taxInfo;
    const addedFee = stripeInfo ? Math.round((organizerFee + stripeFeeForNet(discountedTotal + organizerFee, stripeInfo)) * 100) / 100 : 0;
    const feeLabel = (currentCart as any[]).find((item) => item?.event?.taxInfo?.feeMode === 'added')?.event?.taxInfo?.resolvedFee?.feeLabel || 'Service Fee';
    const total = discountedTotal + addedFee;

    const isEmpty = currentCart.length === 0;

    const waiverRequiredEvents = (currentCart as any[])
        .map((item) => ({
            eventId: String(item?.eventId || item?.event?._id || item?.id || ''),
            title: String(item?.event?.title || ''),
            enabled: Boolean(item?.event?.liabilityWaiverEnabled),
            text: String(item?.event?.liabilityWaiverText ?? ''),
        }))
        .filter((e) => e.enabled)
        .filter((e, idx, arr) => arr.findIndex((x) => x.eventId === e.eventId) === idx);

    const allWaiversAccepted = waiverRequiredEvents.every((e) => acceptedWaiversByEventId[e.eventId] === true);
    const checkoutDisabled = isEmpty || checkoutMutation.status === 'pending' || !allWaiversAccepted;

    if (isLoading) {
        return (
            <div className={`min-h-screen ${pageBg}`}>
                <Header />
                <div className={`${wrap} flex min-h-[70vh] flex-col items-center justify-center`}>
                    <div className="text-xl font-bold tracking-[-0.02em] text-grey40 dark:text-nightMuted">Loading cart...</div>
                </div>
                <Footer hasMobileNav />
                <MobileBottomNav />
            </div>
        );
    }
    if (isError) {
        return (
            <div className={`min-h-screen ${pageBg}`}>
                <Header />
                <div className={`${wrap} flex min-h-[70vh] flex-col items-center justify-center`}>
                    <div className="text-lg font-semibold text-red-500 dark:text-red-400">Failed to load cart. <button className="underline underline-offset-4" onClick={() => refetch()}>Retry</button></div>
                </div>
                <Footer hasMobileNav />
                <MobileBottomNav />
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${pageBg}`}>
            <Header />
            <section className="relative isolate overflow-hidden">
                <div className={glow} />
                <div className={`${wrap} pt-10 sm:pt-16`}>
                    <p className={eyebrow}>Checkout</p>
                    <h1 className={`${displayTitle} mt-4`}>Your cart</h1>
                </div>
            </section>
            <div className="w-full min-h-[70vh] flex flex-col items-center pt-8 pb-10 md:pt-12 md:pb-16">
                <div className={`${wrap} flex flex-col md:flex-row gap-6 md:gap-8`}>
                    <div className="flex-1 min-w-0 flex flex-col gap-6">
                        <div className={`${cardSurface} p-2 md:p-4`}>
                            <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted mb-2 h-12 bg-grey99 dark:bg-white/[0.04] items-center rounded-2xl px-4 py-2">
                                <div className="col-span-6">Event</div>
                                <div className="col-span-2 text-left ml-2">Quantity</div>
                                <div className="col-span-2 text-center ml-11">Price</div>
                                <div className="col-span-2 text-center"></div>
                            </div>
                            {isLoading ? (
                                // Loading skeleton for cart items
                                <div className="space-y-4 px-4 md:px-1 py-4">
                                    {[1, 2].map((i) => (
                                        <div key={i} className="border-b border-grey90 last:border-b-0 dark:border-white/10 pb-4 animate-pulse">
                                            <div className="flex md:hidden gap-3 items-start">
                                                <div className="w-22 h-22 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                <div className="flex-1 space-y-2">
                                                    <div className="h-4 bg-grey99 dark:bg-white/10 rounded-xl w-3/4"></div>
                                                    <div className="h-3 bg-grey99 dark:bg-white/10 rounded-xl w-1/2"></div>
                                                    <div className="h-4 bg-grey99 dark:bg-white/10 rounded-xl w-1/4"></div>
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <div className="w-7 h-7 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                        <div className="w-6 h-6 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                        <div className="w-7 h-7 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="hidden md:grid grid-cols-12 items-center gap-2">
                                                <div className="col-span-6 flex items-center gap-3">
                                                    <div className="w-16 h-16 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                    <div className="space-y-2 flex-1">
                                                        <div className="h-4 bg-grey99 dark:bg-white/10 rounded-xl w-3/4"></div>
                                                        <div className="h-3 bg-grey99 dark:bg-white/10 rounded-xl w-1/2"></div>
                                                    </div>
                                                </div>
                                                <div className="col-span-2 flex items-center justify-center gap-2">
                                                    <div className="w-7 h-7 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                    <div className="w-6 h-6 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                    <div className="w-7 h-7 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                </div>
                                                <div className="col-span-2">
                                                    <div className="h-4 bg-grey99 dark:bg-white/10 rounded-xl w-16 ml-auto"></div>
                                                </div>
                                                <div className="col-span-2 flex justify-center">
                                                    <div className="w-5 h-5 bg-grey99 dark:bg-white/10 rounded-xl"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : isEmpty ? (
                                <EmptyState
                                    title="Your cart is empty."
                                    action={<a href="/" className={btnPrimary}>Explore Events</a>}
                                />
                            ) : (
                                currentCart.map((item, i) => {
                                    // Add eventId and ticketId for API
                                    const eventId = item.eventId || item.event?._id || item.eventId || item.id;
                                    const ticketId = item.ticketId || item.ticket?._id || item.ticketId;
                                    const selectedDate = item.selectedDate; // <-- Assume this exists on item
                                    const seatUuid = item.seatUuid;

                                    const renderAttendeeFields = () => {
                                        if (!perTicketInfoEnabled) return null;
                                        const slots = getItemAttendeeSlots(item);
                                        const fieldsCount = slots.length;
                                        return (
                                            <div className={`${surface} mt-4 rounded-2xl p-4`}>
                                                <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted mb-1">Attendee info</div>
                                                <div className="space-y-3 divide-y-1">
                                                    {slots.map((slot) => {
                                                        const { key, index: idx } = slot;
                                                        const val = attendeeInfos[key] || { ...slot, name: '', email: '', phone: '' };
                                                        return (
                                                            <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-3">
                                                                <Input
                                                                    variant="flat"
                                                                    size="sm"
                                                                    classNames={fieldClassNames}
                                                                    placeholder={`Attendee name${fieldsCount > 1 ? ` #${idx + 1}` : ''}`}
                                                                    value={val.name}
                                                                    onChange={(e) => setAttendeeInfos(prev => ({
                                                                        ...prev,
                                                                        [key]: { ...val, name: e.target.value }
                                                                    }))}
                                                                />
                                                                <Input
                                                                    variant="flat"
                                                                    size="sm"
                                                                    classNames={fieldClassNames}
                                                                    placeholder="Attendee email (optional)"
                                                                    value={val.email}
                                                                    onChange={(e) => setAttendeeInfos(prev => ({
                                                                        ...prev,
                                                                        [key]: { ...val, email: e.target.value }
                                                                    }))}
                                                                />
                                                                <Input
                                                                    variant="flat"
                                                                    size="sm"
                                                                    classNames={fieldClassNames}
                                                                    placeholder="Phone (optional)"
                                                                    value={val.phone || ''}
                                                                    onChange={(e) => setAttendeeInfos(prev => ({
                                                                        ...prev,
                                                                        [key]: { ...val, phone: e.target.value }
                                                                    }))}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    };
                                    return (
                                        <div key={item.id} className="border-b border-grey90 last:border-b-0 dark:border-white/10 px-3 md:px-4 py-4 md:py-5">
                                            {/* Mobile layout: image left, all content right in one row */}
                                            <div className="flex md:hidden gap-3 items-start">
                                                <a href={`/event/${item.event._id}`}>
                                                    <img src={item?.event?.attachments?.banner?.link} alt={item?.event?.title} width={88} height={88} className="rounded-2xl object-cover aspect-square w-22 h-22 md:w-16 md:h-16" />
                                                </a>
                                                <div className="flex-1 min-w-0 flex flex-col gap-1">
                                                    {/* First row: title only */}
                                                    <a href={`/event/${item.event._id}`}>
                                                        <div className="font-bold text-base tracking-[-0.02em] truncate text-grey10 dark:text-white">{item?.event?.title}</div>
                                                    </a>
                                                    {/* Ticket name display */}
                                                    {item?.ticket?.name && (
                                                        <div className="text-xs text-grey40 dark:text-nightMuted font-medium mt-1">
                                                            {item.ticket.name}
                                                            {item.seatNumber && (
                                                                <span className="text-primary dark:text-nightAccent font-semibold ml-2">
                                                                    • Seat {item.seatNumber}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Second row: fee/warning left, price right */}
                                                    <div className="flex items-center w-full">
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-grey10 dark:text-white text-base whitespace-nowrap">{cur}{Number(item?.ticket?.price || 0).toFixed(2)}</span>
                                                                {(() => {
                                                                    const { memberContribution, promoContribution } = getItemDiscountPerUnit(item);
                                                                    const discountPerUnit = memberContribution + promoContribution;
                                                                    if (discountPerUnit > 0) {
                                                                        return (
                                                                            <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                                                                                -{cur}{discountPerUnit.toFixed(2)} off
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}
                                                            </div>
                                                        </div>
                                                        {item.warning && <span className="text-red-500 dark:text-red-400 font-semibold ml-2">{item.warning}</span>}
                                                    </div>
                                                    {/* Third row: quantity left, delete right */}
                                                    <div className="flex items-center mt-2 w-full">
                                                        <div className="flex items-center gap-2">
                                                            {!item.seatUuid && (
                                                                <>
                                                                    <button className={qtyBtn} onClick={() => decreaseMutation.mutate({ eventId, ticketId, selectedDate })} disabled={item.quantity <= 1 || decreaseMutation.status === 'pending'}><Minus /></button>
                                                                    <span className="w-6 text-center font-bold text-base text-grey10 dark:text-white">{item.quantity}</span>
                                                                    <button className={qtyBtn} onClick={() => increaseMutation.mutate({ eventId, ticketId, selectedDate })} disabled={increaseMutation.status === 'pending'}><Plus /></button>
                                                                </>
                                                            )}
                                                            {item.seatUuid && (
                                                                <span className="rounded-full bg-grey99 px-3 py-1 text-xs font-bold uppercase tracking-[0.1em] text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Seat</span>
                                                            )}
                                                        </div>
                                                        <button className={`${removeBtn} flex-shrink-0 ml-auto`} title="Remove" onClick={() => removeMutation.mutate({ eventId, ticketId, selectedDate })} disabled={removeMutation.status === 'pending'}>
                                                            <Trash />
                                                        </button>
                                                    </div>
                                                    {/* {renderAttendeeFields()} */}
                                                </div>
                                            </div>
                                            {/* Desktop layout */}
                                            <div className="hidden md:grid grid-cols-12 items-center gap-2">
                                                <a href={`/event/${item.event._id}`} className="col-span-6 flex items-center gap-3 min-w-0">
                                                    <div className="col-span-6 flex items-center gap-3 min-w-0">
                                                        <img src={item?.event?.attachments?.banner?.link} alt={item?.event?.title} width={64} height={64} className="rounded-2xl object-cover aspect-square w-20 h-20" />
                                                        <div className="min-w-0">
                                                            <div className="font-bold text-base tracking-[-0.02em] truncate text-grey10 dark:text-white">{item?.event?.title}</div>
                                                            {/* Ticket name display */}
                                                            {item?.ticket?.name && (
                                                                <div className="text-xs text-grey40 dark:text-nightMuted font-medium mt-1">
                                                                    {item.ticket.name}
                                                                    {item.seatNumber && (
                                                                        <span className="text-primary dark:text-nightAccent font-semibold ml-2">
                                                                            • Seat {item.seatNumber}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            <div className="text-xs font-semibold text-grey40 dark:text-nightMuted mt-1">
                                                                {cur}{Number(item?.ticket?.price || 0).toFixed(2)}
                                                                {item.warning && <span className="text-red-500 dark:text-red-400 font-semibold ml-2">{item.warning}</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </a>
                                                <div className="col-span-2 flex items-center justify-center gap-2">
                                                    {!item.seatUuid && (
                                                        <>
                                                            <button className={qtyBtn} onClick={() => decreaseMutation.mutate({ eventId, ticketId, selectedDate })} disabled={item.quantity <= 1 || decreaseMutation.status === 'pending'}><Minus /></button>
                                                            <span className="w-6 text-center font-bold text-base text-grey10 dark:text-white">{item.quantity}</span>
                                                            <button className={qtyBtn} onClick={() => increaseMutation.mutate({ eventId, ticketId, selectedDate })} disabled={increaseMutation.status === 'pending'}><Plus /></button>
                                                        </>
                                                    )}
                                                    {item.seatUuid && (
                                                        <span className="rounded-full bg-grey99 px-3 py-1 text-xs font-bold uppercase tracking-[0.1em] text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Seat</span>
                                                    )}
                                                </div>
                                                <div className="col-span-2 text-right font-bold text-grey10 dark:text-white text-base">
                                                    <div className="flex flex-col items-end">
                                                        <div className="flex items-center gap-2">
                                                            {cur}{(Number(item?.ticket?.price || 0) * item.quantity).toFixed(2)}
                                                            {(() => {
                                                                const { memberContribution, promoContribution } = getItemDiscountPerUnit(item);
                                                                const lineDiscount = (memberContribution + promoContribution) * item.quantity;
                                                                if (lineDiscount > 0) {
                                                                    return (
                                                                        <span className="text-green-600 dark:text-green-400 text-xs font-medium">
                                                                            -{cur}{lineDiscount.toFixed(2)}
                                                                        </span>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-span-2 flex justify-center">
                                                    <button className={removeBtn} title="Remove" onClick={() => removeMutation.mutate({ eventId, ticketId, selectedDate })} disabled={removeMutation.status === 'pending'}>
                                                        <Trash />
                                                    </button>
                                                </div>
                                            </div>
                                            {renderAttendeeFields()}
                                        </div>
                                    )
                                })
                            )}
                        </div>



                        {/* Promo Code Section - Moved under checkout */}
                        <div className={`${cardSurface} p-5 md:p-8`}>
                            <div className={`${cardTitle} mb-5`}>Promo Code</div>
                            <div className="space-y-3">
                                {!appliedPromoCode ? (
                                    <div className="flex items-start gap-2">
                                        <Input
                                            placeholder="Enter promo code"
                                            value={promoCode}
                                            onChange={handlePromoCodeChange}
                                            className="flex-1"
                                            classNames={fieldClassNames}
                                            isInvalid={!!promoCodeError}
                                            errorMessage={promoCodeError}
                                        />
                                        <button
                                            onClick={() => validatePromoCodeAsync(promoCode)}
                                            disabled={!promoCode.trim() || isValidatingPromo}
                                            className={`${btnPrimary} shrink-0 disabled:cursor-not-allowed`}
                                        >
                                            {isValidatingPromo ? 'Validating...' : 'Apply'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between gap-3 rounded-2xl bg-green-50 p-4 ring-1 ring-green-200 dark:bg-green-900/20 dark:ring-green-800">
                                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-green-800 dark:text-green-400 font-medium">
                                                {appliedPromoCode.promoCode?.code} applied
                                            </span>
                                            <span className="text-green-600 dark:text-green-400 text-sm">
                                                -{cur}{promoDiscount.toFixed(2)} discount
                                                {appliedPromoCode.discountType === 'percentage'
                                                    ? ` (${appliedPromoCode.discountValue}% off)`
                                                    : ` (${cur}${appliedPromoCode.discountValue} off)`
                                                }
                                            </span>
                                        </div>
                                        <button
                                            onClick={removePromoCode}
                                            className="shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold text-green-700 ring-1 ring-green-300 transition-colors hover:bg-green-100 dark:text-green-400 dark:ring-green-700 dark:hover:bg-green-900/30"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {memberPricingDiscount > 0 && (
                            <div className={`${cardSurface} p-5 md:p-6`}>
                                <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-green-50 p-4 ring-1 ring-green-200 dark:bg-green-900/20 dark:ring-green-800">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span className="text-green-800 font-medium dark:text-green-400">Member discount applied</span>
                                    <span className="text-green-600 text-sm dark:text-green-400">-{cur}{memberPricingDiscount.toFixed(2)}</span>
                                </div>
                            </div>
                        )}

                        {/* Personal Details: Show for guests, hidden for authenticated users */}
                        {isGuest && (
                            <div className={`${cardSurface} p-5 md:p-8`}>
                                <div className={`${cardTitle} mb-5`}>Your Information</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={fieldLabel}>Name *</label>
                                        <Input
                                            variant="flat"
                                            size="sm"
                                            placeholder="Name"
                                            value={details.name}
                                            isInvalid={showValidationErrors && !!validationErrors.name}
                                            errorMessage={showValidationErrors && validationErrors.name}
                                            onChange={e => {
                                                setDetails(d => ({ ...d, name: e.target.value }));
                                                if (showValidationErrors && validationErrors.name) {
                                                    setValidationErrors(prev => ({ ...prev, name: undefined }));
                                                }
                                            }}
                                            classNames={fieldClassNames}
                                        />

                                    </div>
                                    <div>
                                        <label className={fieldLabel}>Last name *</label>
                                        <Input
                                            variant="flat"
                                            size="sm"
                                            placeholder="Last name"
                                            value={details.last}
                                            isInvalid={showValidationErrors && !!validationErrors.last}
                                            errorMessage={showValidationErrors && validationErrors.last}
                                            onChange={e => {
                                                setDetails(d => ({ ...d, last: e.target.value }));
                                                if (showValidationErrors && validationErrors.last) {
                                                    setValidationErrors(prev => ({ ...prev, last: undefined }));
                                                }
                                            }}
                                            classNames={fieldClassNames}
                                        />

                                    </div>
                                    <div>
                                        <label className={fieldLabel}>Email *</label>
                                        <Input
                                            variant="flat"
                                            size="sm"
                                            type="email"
                                            placeholder="e.g. name@gmail.com"
                                            value={details.email}
                                            isInvalid={showValidationErrors && !!validationErrors.email}
                                            errorMessage={showValidationErrors && validationErrors.email}
                                            onChange={e => {
                                                setDetails(d => ({ ...d, email: e.target.value }));
                                                if (showValidationErrors && validationErrors.email) {
                                                    setValidationErrors(prev => ({ ...prev, email: undefined }));
                                                }
                                            }}
                                            classNames={fieldClassNames}
                                        />

                                    </div>
                                    <div>
                                        <label className={fieldLabel}>Phone *</label>
                                        <Input
                                            variant="flat"
                                            size="sm"
                                            type="tel"
                                            placeholder="e.g. +31 0123 456 78"
                                            value={details.phone}
                                            isInvalid={showValidationErrors && !!validationErrors.phone}
                                            errorMessage={showValidationErrors && validationErrors.phone}
                                            onChange={e => {
                                                setDetails(d => ({ ...d, phone: e.target.value }));
                                                if (showValidationErrors && validationErrors.phone) {
                                                    setValidationErrors(prev => ({ ...prev, phone: undefined }));
                                                }
                                            }}
                                            classNames={fieldClassNames}
                                        />

                                    </div>
                                </div>
                                {/* Agreement text after input fields on mobile only */}
                                <div className="text-xs text-grey40 dark:text-nightMuted mt-5 text-center md:hidden">
                                    By purchasing you agree to our general <a href="/terms-conditions" className="text-grey10 dark:text-white underline underline-offset-2 font-semibold">Term of Use</a>, <a href="/privacy-policy" className="text-grey10 dark:text-white underline underline-offset-2 font-semibold">Privacy Policy</a>.
                                </div>
                            </div>
                        )}
                        {/* User info display for authenticated users */}
                        {!isGuest && user && (
                            <div className={`${cardSurface} p-5 md:p-8`}>
                                <div className={`${cardTitle} mb-5`}>Your Information</div>

                                {/* Toggle for custom info */}
                                <div className="mb-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <Checkbox checked={useCustomInfo} onValueChange={checked => setUseCustomInfo(!!checked)} classNames={{
                                            icon: 'text-white'
                                        }} />
                                        {/* <input
                                            type="checkbox"
                                            checked={useCustomInfo}
                                            onChange={(e) => setUseCustomInfo(e.target.checked)}
                                            className="rounded border-gray-300 text-primary40 focus:ring-primary40"
                                        /> */}
                                        <span className="text-sm font-medium text-grey20 dark:text-nightTextSecondary">Use custom order information</span>
                                    </label>
                                </div>

                                {/* Toggle for per-ticket attendee info (optional/required by event) */}
                                {requiredTicketInfoMode > 0 && (
                                    <div className="mb-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <Checkbox
                                                checked={perTicketInfoEnabled}
                                                isDisabled={requiredTicketInfoMode === 2}
                                                onValueChange={checked => setUsePerTicketInfo(!!checked)}
                                                classNames={{ icon: 'text-white' }}
                                            />
                                            <span className="text-sm font-medium text-grey20 dark:text-nightTextSecondary">
                                                {requiredTicketInfoMode === 2 ? 'Attendee info per ticket is required for this event' : 'Enter attendee info per ticket'}
                                            </span>
                                        </label>
                                    </div>
                                )}

                                {!useCustomInfo ? (
                                    // Display account info
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={fieldLabel}>Name</label>
                                            <div className="flex h-12 w-full items-center truncate rounded-2xl bg-grey99 px-4 text-sm text-grey10 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-white dark:ring-white/10">
                                                {user.firstName || 'Not provided'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className={fieldLabel}>Email</label>
                                            <div className="flex h-12 w-full items-center truncate rounded-2xl bg-grey99 px-4 text-sm text-grey10 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-white dark:ring-white/10">
                                                {user.email || 'Not provided'}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Custom info form
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={fieldLabel}>Name *</label>
                                            <Input
                                                variant="flat"
                                                size="sm"
                                                placeholder="Name"
                                                value={details.name}
                                                isInvalid={showValidationErrors && !!validationErrors.name}
                                                errorMessage={showValidationErrors && validationErrors.name}
                                                onChange={e => {
                                                    setDetails(d => ({ ...d, name: e.target.value }));
                                                    if (showValidationErrors && validationErrors.name) {
                                                        setValidationErrors(prev => ({ ...prev, name: undefined }));
                                                    }
                                                }}
                                                classNames={fieldClassNames}
                                            />

                                        </div>
                                        <div>
                                            <label className={fieldLabel}>Last name *</label>
                                            <Input
                                                variant="flat"
                                                size="sm"
                                                placeholder="Last name"
                                                value={details.last}
                                                isInvalid={showValidationErrors && !!validationErrors.last}
                                                errorMessage={showValidationErrors && validationErrors.last}
                                                onChange={e => {
                                                    setDetails(d => ({ ...d, last: e.target.value }));
                                                    if (showValidationErrors && validationErrors.last) {
                                                        setValidationErrors(prev => ({ ...prev, last: undefined }));
                                                    }
                                                }}
                                                classNames={fieldClassNames}
                                            />

                                        </div>
                                        <div>
                                            <label className={fieldLabel}>Email *</label>
                                            <Input
                                                variant="flat"
                                                size="sm"
                                                type="email"
                                                placeholder="e.g. name@gmail.com"
                                                value={details.email}
                                                isInvalid={showValidationErrors && !!validationErrors.email}
                                                errorMessage={showValidationErrors && validationErrors.email}
                                                onChange={e => {
                                                    setDetails(d => ({ ...d, email: e.target.value }));
                                                    if (showValidationErrors && validationErrors.email) {
                                                        setValidationErrors(prev => ({ ...prev, email: undefined }));
                                                    }
                                                }}
                                                classNames={fieldClassNames}
                                            />

                                        </div>
                                        <div>
                                            <label className={fieldLabel}>Phone *</label>
                                            <Input
                                                variant="flat"
                                                size="sm"
                                                type="tel"
                                                placeholder="e.g. +31 0123 456 78"
                                                value={details.phone}
                                                isInvalid={showValidationErrors && !!validationErrors.phone}
                                                errorMessage={showValidationErrors && validationErrors.phone}
                                                onChange={e => {
                                                    setDetails(d => ({ ...d, phone: e.target.value }));
                                                    if (showValidationErrors && validationErrors.phone) {
                                                        setValidationErrors(prev => ({ ...prev, phone: undefined }));
                                                    }
                                                }}
                                                classNames={fieldClassNames}
                                            />

                                        </div>
                                    </div>
                                )}

                                <div className="text-xs text-grey40 dark:text-nightMuted mt-5">
                                    {!useCustomInfo ? (
                                        <>Your account information will be used for this order. To update your details, visit your <a href="/profile" className="text-grey10 dark:text-white underline underline-offset-2 font-semibold">profile settings</a>.</>
                                    ) : (
                                        <>Custom information will be used for this order. You can toggle back to use your account information above.</>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className='sticky self-start bottom-0 left-0 right-0 z-10 w-full md:static'>
                            <div className="md:hidden rounded-[28px] bg-white ring-1 ring-black/[0.06] shadow-[0_-12px_40px_-20px_rgba(0,0,0,0.25)] dark:bg-nightSurface dark:ring-white/10">
                                <div className="px-5 pt-5">
                                    <div className="text-sm text-grey40 dark:text-nightMuted">
                                        <div className="flex justify-between py-1"><span>Subtotal:</span><span>{isEmpty ? '-' : `${cur}${subtotal.toFixed(2)}`}</span></div>
                                        {promoDiscount > 0 && (
                                            <div className="flex justify-between py-1 text-green-600 dark:text-green-400"><span>Discount:</span><span>-{cur}{promoDiscount.toFixed(2)}</span></div>
                                        )}
                                        {memberPricingDiscount > 0 && (
                                            <div className="flex justify-between py-1 text-green-600 dark:text-green-400"><span>Member Discount:</span><span>-{cur}{memberPricingDiscount.toFixed(2)}</span></div>
                                        )}
                                        {addedFee > 0 && (
                                            <div className="flex justify-between py-1"><span>{feeLabel}:</span><span>{cur}{(Math.round(addedFee * 100) / 100).toFixed(2)}</span></div>
                                        )}
                                        <div className="flex items-baseline justify-between mt-3 border-t border-grey90 pt-3 text-grey10 dark:border-white/10 dark:text-white"><span className="text-base font-semibold">Total:</span><span className="text-2xl font-bold tracking-[-0.03em]">{isEmpty ? '-' : `${cur}${total.toFixed(2)}`}</span></div>
                                    </div>
                                </div>
                                {waiverRequiredEvents.length > 0 && (
                                    <div className="px-5 mt-4">
                                        <div className="font-bold text-base mb-2 tracking-[-0.02em] text-grey10 dark:text-white">Liability Waiver</div>
                                        <div className="space-y-4">
                                            {waiverRequiredEvents.map((e) => (
                                                <div key={e.eventId} className="rounded-2xl p-3 ring-1 ring-black/[0.06] dark:ring-white/10">
                                                    <div className="text-sm font-semibold text-grey10 dark:text-white mb-2">{e.title}</div>
                                                    <div className="max-h-[180px] overflow-auto bg-grey99 dark:bg-white/[0.04] rounded-xl p-3 text-sm text-grey30 dark:text-nightMuted whitespace-pre-wrap">
                                                        {String(e.text || '').trim()}
                                                    </div>
                                                    <label className="flex items-center gap-2 cursor-pointer mt-3">
                                                        <Checkbox
                                                            checked={acceptedWaiversByEventId[e.eventId] === true}
                                                            onValueChange={(checked) => {
                                                                setAcceptedWaiversByEventId((prev) => ({
                                                                    ...prev,
                                                                    [e.eventId]: !!checked,
                                                                }));
                                                            }}
                                                            classNames={{ icon: 'text-white' }}
                                                        />
                                                        <span className="text-sm font-medium text-grey20 dark:text-nightTextSecondary">I agree to the liability waiver</span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Sticky checkout button for mobile */}
                                <div className="px-5 pb-5 pt-4">
                                    <button className={`${btnAccent} h-14 w-full text-base`} disabled={checkoutDisabled} onClick={() => checkoutMutation.mutate()}>Checkout</button>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Desktop summary on the right */}
                    <div className="hidden md:block w-full md:w-[380px] flex-shrink-0 sticky self-start top-[96px]">
                        <div className={`${surface} p-8 w-full`}>
                            <div className={`${cardTitle} mb-6`}>Order summary</div>
                            <div className="text-sm text-grey40 dark:text-nightMuted">
                                <div className="flex justify-between py-1"><span>Subtotal:</span><span>{isEmpty ? '-' : `${cur}${subtotal.toFixed(2)}`}</span></div>
                                {promoDiscount > 0 && (
                                    <div className="flex justify-between py-1 text-green-600 dark:text-green-400"><span>Discount:</span><span>-{cur}{promoDiscount.toFixed(2)}</span></div>
                                )}
                                {memberPricingDiscount > 0 && (
                                    <div className="flex justify-between py-1 text-green-600 dark:text-green-400"><span>Member Discount:</span><span>-{cur}{memberPricingDiscount.toFixed(2)}</span></div>
                                )}
                                {addedFee > 0 && (
                                    <div className="flex justify-between py-1"><span>{feeLabel}:</span><span>{cur}{(Math.round(addedFee * 100) / 100).toFixed(2)}</span></div>
                                )}
                                <div className="flex items-baseline justify-between mt-4 border-t border-grey90 pt-4 text-grey10 dark:border-white/10 dark:text-white"><span className="text-base font-semibold">Total:</span><span className="text-[32px] font-bold leading-none tracking-[-0.03em]">{isEmpty ? '-' : `${cur}${total.toFixed(2)}`}</span></div>
                            </div>
                            <button
                                className={`${btnAccent} h-14 w-full text-base mt-8`}
                                disabled={checkoutDisabled}
                                onClick={() => checkoutMutation.mutate()}
                            >
                                {checkoutMutation.status === 'pending' ? 'Processing...' : 'Checkout'}
                            </button>

                            {waiverRequiredEvents.length > 0 && (
                                <div className="mt-6 border-t border-grey90 dark:border-white/10 pt-6">
                                    <div className="font-bold text-base mb-3 tracking-[-0.02em] text-grey10 dark:text-white">Liability Waiver</div>
                                    <div className="space-y-4">
                                        {waiverRequiredEvents.map((e) => (
                                            <div key={e.eventId} className="rounded-2xl p-3 ring-1 ring-black/[0.06] dark:ring-white/10">
                                                <div className="text-sm font-semibold text-grey10 dark:text-white mb-2">{e.title}</div>
                                                <div className="max-h-[220px] overflow-auto bg-grey99 dark:bg-white/[0.04] rounded-xl p-3 text-sm text-grey30 dark:text-nightMuted whitespace-pre-wrap">
                                                    {String(e.text || '').trim()}
                                                </div>
                                                <label className="flex items-center gap-2 cursor-pointer mt-3">
                                                    <Checkbox
                                                        checked={acceptedWaiversByEventId[e.eventId] === true}
                                                        onValueChange={(checked) => {
                                                            setAcceptedWaiversByEventId((prev) => ({
                                                                ...prev,
                                                                [e.eventId]: !!checked,
                                                            }));
                                                        }}
                                                        classNames={{ icon: 'text-white' }}
                                                    />
                                                    <span className="text-sm font-medium text-grey20 dark:text-nightTextSecondary">I agree to the liability waiver</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Desktop Promo Code Section - Moved under checkout */}
                            {/* <div className="mt-4">
                                <div className="bg-white rounded shadow-eventopia p-4 md:p-6">
                                    <div className="font-bold text-base md:text-lg mb-4 text-[#B1B5C3]">Promo Code</div>
                                    <div className="space-y-3">
                                        {!appliedPromoCode ? (
                                            <div className="flex gap-2">
                                                <Input
                                                    placeholder="Enter promo code"
                                                    classNames={inputClassNames}
                                                    value={promoCode}
                                                    onChange={handlePromoCodeChange}
                                                    className="flex-1"
                                                    isInvalid={!!promoCodeError}
                                                    errorMessage={promoCodeError}
                                                />
                                                <button
                                                    onClick={() => validatePromoCodeAsync(promoCode)}
                                                    disabled={!promoCode.trim() || isValidatingPromo}
                                                    className="px-4 py-2 bg-primary40 text-white rounded hover:bg-primary50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isValidatingPromo ? 'Validating...' : 'Apply'}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                    <span className="text-green-800 font-medium">
                                                        {appliedPromoCode.promoCode?.code} applied
                                                    </span>
                                                    <span className="text-green-600 text-sm">
                                                        -{cur}{appliedPromoCode.discountAmount.toFixed(2)} discount
                                                        {appliedPromoCode.discountType === 'percentage' 
                                                            ? ` (${appliedPromoCode.discountValue}% off)`
                                                            : ` (${cur}${appliedPromoCode.discountValue} off)`
                                                        }
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={removePromoCode}
                                                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div> */}

                            {/* Agreement text after summary on desktop only */}
                            <div className="text-xs text-grey40 dark:text-nightMuted mt-4 text-center hidden md:block">
                                By purchasing you agree to our general <a href="/terms-conditions" className="text-grey10 dark:text-white underline underline-offset-2 font-semibold">Term of Use</a>, <a href="/privacy-policy" className="text-grey10 dark:text-white underline underline-offset-2 font-semibold">Privacy Policy</a>.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
} 